import { apiFetch } from '@/lib/api';

export type PromptKey = 'extraction' | 'review';

export interface PromptWithVersions {
  current: { content: string; version: number };
  versions: Array<{ id: string; version: number; content: string; created_at: string }>;
}

export interface ManifestPromptsData {
  extraction: PromptWithVersions;
  review: PromptWithVersions;
}

export class ManifestPromptsService {
  async getAll(): Promise<ManifestPromptsData> {
    const res = await apiFetch('/api/settings/manifest-prompts');
    if (!res.ok) throw new Error('Failed to fetch manifest prompts');
    const json = await res.json();
    return json.data;
  }

  async get(key: PromptKey): Promise<PromptWithVersions> {
    const res = await apiFetch(`/api/settings/manifest-prompts/${key}`);
    if (!res.ok) throw new Error('Failed to fetch prompt');
    const json = await res.json();
    return json.data;
  }

  async update(key: PromptKey, content: string): Promise<{ version: number }> {
    const res = await apiFetch(`/api/settings/manifest-prompts/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ content }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error?.message || 'Failed to update prompt');
    }
    const json = await res.json();
    return json.data;
  }

  async revert(key: PromptKey, version: number): Promise<{ version: number }> {
    const res = await apiFetch(`/api/settings/manifest-prompts/${key}/revert`, {
      method: 'POST',
      body: JSON.stringify({ version }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.error?.message || 'Failed to revert');
    }
    const json = await res.json();
    return json.data;
  }

  async seed(): Promise<{ extraction: boolean; review: boolean }> {
    const res = await apiFetch('/api/settings/manifest-prompts/seed', { method: 'POST' });
    if (!res.ok) throw new Error('Failed to seed prompts');
    const json = await res.json();
    return json.data;
  }
}
